import { CenteredSpinner } from '@stacc/flow-ui-components'
import { Modal } from '@stacc/flow-ui-components'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import fileService from '../../services/files'
import { DocumentsCard } from './DocumentCard'
import type { TaskProps } from './TaskPropsTypes'
import { UploadedDocumentHandler } from './UploadedDocumentHandler'
import { FlowContext } from './useFlowContext'

const MAX_SIZE = 20 * 1024 * 1024

export function Files(props: TaskProps) {
  const { flow, t } = props
  const { flowId } = flow

  const [file, setFile] = useState([])
  const [modal, setModal] = useState(false)
  const [isDragOver, setIsDragOver] = useState(0)

  function handleFileUpload(file: any) {
    setFile(file)
    setModal(true)
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles: any) => {
      handleFileUpload(
        //@ts-ignore
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        )
      )
    },
    accept: {
      'application/pdf': [],
      'image/*': []
    },
    noClick: true,
    noKeyboard: true,
    multiple: false,
    maxSize: MAX_SIZE
  })
  const { data: uploadedFiles, isFetching } = useQuery({
    queryKey: [
      'files',
      flowId,
      flow.data.documentRequirements,
      flow.data.internalFiles
    ],
    queryFn: async () => {
      const { documentRequirements, internalFiles, storedFiles } = flow.data
      const fetchFiles = async (filesSource: any, typeKey: string) => {
        type uploadedByType = 'customer' | 'caseworker' | 'dealer' | 'flow'
        let uploadedBy: uploadedByType

        let files: any[] = []
        for (const key in filesSource) {
          const document = filesSource[key]
          const fileId = document.fileId ?? document.storeID
          uploadedBy = 'caseworker'
          if (fileId) {
            try {
              if (document.uploadedBySub) {
                uploadedBy = 'customer'
              }

              if (document.isUploadedByDealer) {
                uploadedBy = 'dealer'
              }
              
              if(document.uploadedByFlow){
                uploadedBy = 'flow'
              }

              const file = await fileService.getFile(fileId)
              const metadata = await fileService.getFileMetadata(fileId)
              files.push({
                file: file?.file,
                mimeType: file?.type,
                type: typeKey === 'key' ? key : document.type,
                fileName: metadata?.data.filename,
                created: metadata?.data.created,
                fileId: fileId,
                uploadedBy
              })
            } catch (err) {}
          }
        }
        return files
      }

      const documentFilesFormat = await fetchFiles(documentRequirements, 'key')
      const internalFilesFormat = await fetchFiles(internalFiles, 'type')

      return {
        flowId,
        files: documentFilesFormat,
        internalFiles: internalFilesFormat
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const { files = [], internalFiles = [] } = uploadedFiles || {}

  const allFiles = [...files, ...internalFiles]

  const customerDocuments = allFiles.filter((d) => d.uploadedBy === 'customer')
  const caseWorkerDocuments = allFiles.filter(
    (d) => d.uploadedBy === 'caseworker'
  )
  const dealerDocuments = files.filter((d) => d.uploadedBy === 'dealer')
  const remainingDocuments = allFiles.filter(
    (d) => !['customer', 'caseworker', 'dealer'].includes(d.uploadedBy)
  )

  const { ...rootProps } = getRootProps()

  if (isFetching) return <CenteredSpinner />

  return (
    <FlowContext.Provider value={props}>
      <div
        className="relative flex flex-col gap-5"
        onDragEnter={() => setIsDragOver((current) => current + 1)}
        onDragLeave={() => setIsDragOver((current) => current - 1)}
      >
        {isDragOver > 0 && (
          <div
            {...rootProps}
            className={clsx(
              'flex justify-center items-center absolute w-full h-full rounded-xl border-2 border-dashed border-gray-400 backdrop-filter backdrop-blur-lg shadow-lg z-50'
            )}
          >
            <input id={`${document}`} {...getInputProps()} type="file" />
            <p>{t('drop-files')}</p>
          </div>
        )}

        <DocumentsCard
          title={t('customerUploadedDocuments')}
          data={customerDocuments ?? []}
          color="blue"
        />
        <DocumentsCard
          title={t('caseWorkerUploadedDocuments')}
          data={caseWorkerDocuments ?? []}
          color="yellow"
        />
        <DocumentsCard
          title={t('dealerUploadedDocuments')}
          data={dealerDocuments ?? []}
          color="purple"
        />
        <DocumentsCard
          title={t('flowUploadedDocuments')}
          data={remainingDocuments ?? []}
          color="green"
        />
        <div
          className="cursor-pointer flex justify-center"
          onClick={() => open()}
        >
          <span className="text-xs ml-4 border-2 border-dashed border-gray-200 rounded-lg p-2 hover:border-gray-600 hover:shadow-lg">
            {t('click-upload-files')}
          </span>
        </div>
        {modal && (
          <Modal
            title={t('uploaded-file')}
            customWidth="80%"
            customHeight="80%"
            onClose={() => {
              setIsDragOver(0)
              setModal(false)
            }}
          >
            <UploadedDocumentHandler
              file={file}
              close={() => {
                setModal(false)
                setIsDragOver(0)
              }}
            />
          </Modal>
        )}
      </div>
    </FlowContext.Provider>
  )
}
